import Storyblok, { StoryblokContent } from '@lib/storyblok'
import { useState } from 'react'
import { StoryData } from 'storyblok-js-client'

const STORYBLOK_CONTENT_TYPE = 'blogpost'
const STORYBLOK_FOLDER = 'blog'

const STORYBLOK_DEFAULT_PARAMS = Object.freeze({
  resolve_relations: [`${STORYBLOK_CONTENT_TYPE}.author`],
})

interface AssetField {
  alt: string
  name: string
  title: string
  filename: string
}

export interface AuthorContent extends StoryblokContent {
  name: string
  photo: AssetField
}

export interface AuthorStory extends StoryData {
  content: AuthorContent
}

export interface BlogPostContent extends StoryblokContent {
  image: string
  intro: string
  title: string
  author: AuthorStory
  published_at: string
  preview_image: {
    alt: string
    filename: string
  } | null
  seo_headline: string | null
  seo_excerpt: string | null
  social_share_image: { alt: string; filename: string } | null
}

export interface BlogPostStory extends StoryData {
  content: BlogPostContent
}

interface StoryblokParams {
  [key: string]: any
}

function buildStoryblokParams(
  params: StoryblokParams = {},
  {
    showDrafts,
  }: {
    showDrafts?: boolean
  }
): StoryblokParams {
  let builtParams: StoryblokParams = {
    ...STORYBLOK_DEFAULT_PARAMS,
  }

  if (showDrafts) {
    builtParams = {
      ...builtParams,
      version: 'draft',
      cv: Date.now(),
    }
  }

  builtParams = {
    ...builtParams,
    ...params,
  }

  return builtParams
}

interface FetchPostsOptions {
  showDrafts?: boolean
  perPage?: number
  page?: number
  storyblokParams?: StoryblokParams
}

export async function fetchPosts(options: FetchPostsOptions = {}) {
  const requestParams = buildStoryblokParams(
    {
      starts_with: `${STORYBLOK_FOLDER}/`,
      per_page: options.perPage,
      page: options.page,
      ...options.storyblokParams,
    },
    {
      showDrafts: options.showDrafts,
    }
  )

  const result = await Storyblok.get('cdn/stories', requestParams)

  return result
}

interface FetchPostOptions {
  showDrafts?: boolean
  slug: string
  storyblokParams?: StoryblokParams
}

export async function fetchPost(options: FetchPostOptions) {
  const requestParams = buildStoryblokParams(options.storyblokParams, {
    showDrafts: options.showDrafts,
  })

  const result = await Storyblok.get(
    `cdn/stories/${STORYBLOK_FOLDER}/${options.slug}`,
    requestParams
  )

  return result
}

export function usePostPaginator({
  initialStories,
  totalStories,
  perPage,
  showDrafts,
  sortBy,
}: {
  initialStories?: BlogPostStory[]
  totalStories: number
  perPage: number
  showDrafts: boolean
  sortBy?: string
}) {
  const [stories, setStories] = useState(initialStories || [])
  const [currentPage, setCurrentPage] = useState(1)
  const [isLoading, setIsLoading] = useState(false)
  const hasMore = stories.length < totalStories

  const fetchMore = async () => {
    const nextPage = currentPage + 1

    setIsLoading(true)
    const result = await fetchPosts({
      page: nextPage,
      perPage,
      showDrafts,
      ...(sortBy && {
        storyblokParams: {
          sort_by: sortBy,
        },
      }),
    })
    setIsLoading(false)

    setCurrentPage(nextPage)
    setStories([...stories, ...result.data.stories])
  }

  return {
    stories,
    fetchMore,
    isLoading,
    hasMore,
  }
}
